import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { servicenowCmdbApi, type CMDBSyncItem, type ServiceNowConfig } from '../api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { RefreshCw, Settings, ArrowUpDown, ArrowLeftRight, EyeOff, AlertTriangle, Database, Link2, Unlink, LogOut, CheckCircle2, Clock } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SetupGuideDialog } from '../components/SetupGuideDialog'
import { RelationshipsTab } from '../components/RelationshipsTab'
import { MatchCIDialog } from '../components/MatchCIDialog'
import { ServiceNowUpdatesDialog } from '../components/ServiceNowUpdatesDialog'
import { useCan } from '@/hooks/useCan'

const STATUS_BADGES: Record<string, { label: string; variant: 'info' | 'warning' | 'danger' | 'success' | 'neutral' | 'outline' }> = {
    new: { label: 'New', variant: 'info' },
    changed: { label: 'Changed', variant: 'warning' },
    // Not deleted from the CMDB: taken out of the MVC — untagged, or gone. Either way it is out of scope.
    deleted: { label: 'Removed from MVC', variant: 'danger' },
    synced: { label: 'Synced', variant: 'success' },
    ignored: { label: 'Ignored', variant: 'neutral' },
    removed: { label: 'Removed', variant: 'neutral' },
}

const FILTER_LABELS: Record<string, string> = {
    all: 'All', new: 'New', changed: 'Changed', deleted: 'Removed from MVC', synced: 'Synced', ignored: 'Ignored', removed: 'Removed',
}

/** Shown on a disabled action: the rule, not just a greyed-out button. */
const MATCH_FIRST = 'Match this CI to a Vault Sentinel object first'

export function ServiceNowCMDBPage() {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    // The screen is open to every servicenow.read. Every change here - the connection, a sync,
    // matching, aligning, applying, ignoring, removing, dismissing - is servicenow.update.
    const canUpdate = useCan('servicenow.update')
    const [showConfig, setShowConfig] = useState(false)
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [page, setPage] = useState(1)
    const [matching, setMatching] = useState<CMDBSyncItem | null>(null)
    const [removing, setRemoving] = useState<CMDBSyncItem | null>(null)
    const [showUpdates, setShowUpdates] = useState(false)

    // Config
    const { data: configRes } = useQuery({
        queryKey: ['servicenow-config'],
        queryFn: async () => (await servicenowCmdbApi.getConfig()).data?.data,
    })
    const config = configRes

    // Config form state
    const [formConfig, setFormConfig] = useState<Partial<ServiceNowConfig>>({})
    const configToEdit = { ...config, ...formConfig }

    const saveConfigMutation = useMutation({
        mutationFn: (data: Partial<ServiceNowConfig>) => servicenowCmdbApi.saveConfig(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['servicenow-config'] })
            toast({ title: 'Saved', description: 'ServiceNow configuration saved.' })
            setShowConfig(false)
            setFormConfig({})
        },
        onError: (err: any) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
    })

    // Stats
    const { data: statsRes } = useQuery({
        queryKey: ['cmdb-stats'],
        queryFn: async () => (await servicenowCmdbApi.getStats()).data?.data,
    })
    const stats = statsRes

    // Items
    const { data: itemsRes, isLoading: itemsLoading } = useQuery({
        queryKey: ['cmdb-items', statusFilter, page],
        queryFn: async () => {
            const res = await servicenowCmdbApi.getItems({ status: statusFilter, page, limit: 50 })
            return res.data
        },
    })

    // Sync log
    const { data: syncLogRes } = useQuery({
        queryKey: ['cmdb-sync-log'],
        queryFn: async () => (await servicenowCmdbApi.getSyncLog()).data?.data,
    })
    const lastSync = syncLogRes?.[0]

    // Sync trigger
    const syncMutation = useMutation({
        mutationFn: () => servicenowCmdbApi.triggerSync(),
        onSuccess: () => {
            toast({ title: 'Sync started', description: 'Fetching data from ServiceNow CMDB...' })
            // Poll for completion
            const poll = setInterval(async () => {
                await queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
                await queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
                await queryClient.invalidateQueries({ queryKey: ['cmdb-sync-log'] })
                const log = await servicenowCmdbApi.getSyncLog()
                const latest = log.data?.data?.[0]
                if (latest?.status !== 'running') {
                    clearInterval(poll)
                    if (latest?.status === 'completed') {
                        toast({ title: 'Sync complete', description: `${latest.totalCIs} CIs processed: ${latest.newItems} new, ${latest.changedItems} changed, ${latest.deletedItems} deleted` })
                    } else {
                        toast({ title: 'Sync failed', description: latest?.errorMessage || 'Unknown error', variant: 'destructive' })
                    }
                }
            }, 3000)
        },
        onError: (err: any) => toast({ title: 'Sync failed', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    // Item actions
    const addMutation = useMutation({
        mutationFn: async (id: number) => (await servicenowCmdbApi.addItem(id)).data?.data,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-servicenow-updates'] })
            if (data?.serviceNowUpdated) {
                toast({ title: 'Aligned', description: `${data.objectId} is aligned with its CI, and ServiceNow now carries its Vault Sentinel ID.` })
            } else if (data?.manual) {
                toast({ title: 'Aligned', description: `${data.objectId} is aligned here. It is on the ServiceNow updates list for your change team to enter.` })
            } else {
                toast({ title: 'Aligned — ServiceNow refused the ID', description: `${data?.objectId}: ${data?.serviceNowError ?? 'unknown error'}. It is on the ServiceNow updates list.`, variant: 'destructive' })
            }
        },
        onError: (err: any) => toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    const updateMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.updateItem(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
            toast({ title: 'Updated', description: 'Object updated from CMDB changes.' })
        },
        onError: (err: any) => toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    const refreshItems = () => {
        queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
        queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
    }
    const errorToast = (err: any) => toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' })

    const unmatchMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.unmatchItem(id),
        onSuccess: refreshItems,
        onError: errorToast,
    })

    const { data: removePreview, isLoading: removePreviewLoading } = useQuery({
        queryKey: ['cmdb-remove-preview', removing?.id],
        queryFn: async () => (await servicenowCmdbApi.getRemovePreview(removing!.id)).data?.data,
        enabled: !!removing,
    })

    const removeMutation = useMutation({
        mutationFn: async (id: number) => (await servicenowCmdbApi.removeItem(id)).data?.data,
        onSuccess: (data) => {
            refreshItems()
            setRemoving(null)
            toast({
                title: 'Removed from the MVC',
                description: `${data?.objectId}: ${data?.dependenciesRemoved} dependenc${data?.dependenciesRemoved === 1 ? 'y' : 'ies'} removed. The object and its versions stay.`
                    + (data?.serviceNowPending ? ' Clearing its ID in ServiceNow is on the ServiceNow updates list.' : ''),
            })
        },
        onError: errorToast,
    })

    const dismissMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.dismissItem(id),
        onSuccess: refreshItems,
        onError: errorToast,
    })

    const dismissNeverAddedMutation = useMutation({
        mutationFn: async () => (await servicenowCmdbApi.dismissNeverAdded()).data?.data,
        onSuccess: (data) => {
            refreshItems()
            toast({ title: 'Dismissed', description: `${data?.dismissed ?? 0} CIs that left the MVC before they were added.` })
        },
        onError: errorToast,
    })

    const ignoreMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.ignoreItem(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
        },
    })

    const items = itemsRes?.data ?? []
    const totalItems = itemsRes?.total ?? 0

    return (
        <div className="space-y-6" style={{ padding: 'var(--spacing-page)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">ServiceNow CMDB</h1>
                    <p className="text-muted-foreground">Sync configuration items from ServiceNow CMDB into Vault Sentinel</p>
                </div>
                {canUpdate && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)}>
                            <Settings className="h-4 w-4 mr-1.5" />
                            Configure
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => syncMutation.mutate()}
                            disabled={syncMutation.isPending || !config?.enabled}
                        >
                            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                            Sync Now
                        </Button>
                    </div>
                )}
            </div>

            {/* Config panel */}
            {canUpdate && showConfig && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">ServiceNow Connection</CardTitle>
                        <CardDescription>Configure the ServiceNow instance and CMDB table to sync from.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Instance URL</Label>
                                <Input
                                    placeholder="https://your-instance.service-now.com"
                                    value={configToEdit.instanceUrl || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, instanceUrl: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>CMDB Table</Label>
                                <Input
                                    placeholder="cmdb_ci"
                                    value={configToEdit.tableName || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, tableName: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Username</Label>
                                <Input
                                    value={configToEdit.username || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, username: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Password / API Key</Label>
                                <Input
                                    type="password"
                                    value={configToEdit.apiKey || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, apiKey: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Vault Sentinel Object ID Field</Label>
                                <Input
                                    placeholder="u_vault_sentinel_id"
                                    value={configToEdit.customFieldVaultSentinel || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, customFieldVaultSentinel: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>MVC Tag Field</Label>
                                <Input
                                    placeholder="u_mvc"
                                    value={configToEdit.customFieldMvc || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, customFieldMvc: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Default Incident Caller</Label>
                                <Input
                                    placeholder="ServiceNow user sys_id"
                                    value={configToEdit.defaultCallerId || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, defaultCallerId: e.target.value }))}
                                />
                                <p className="text-xs text-muted-foreground">ServiceNow user used as the Caller when raising incidents.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Default Assignment Group</Label>
                                <Input
                                    placeholder="ServiceNow group sys_id"
                                    value={configToEdit.defaultAssignmentGroup || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, defaultAssignmentGroup: e.target.value }))}
                                />
                                <p className="text-xs text-muted-foreground">Group incidents are routed to by default.</p>
                            </div>
                        </div>
                        <div className="space-y-2 pt-2">
                            <Label>Vault Sentinel IDs in ServiceNow</Label>
                            <select
                                className="h-9 w-full max-w-xl rounded-md border border-input bg-background px-3 text-sm"
                                value={configToEdit.writeBackMode || 'automatic'}
                                onChange={(e) => setFormConfig(p => ({ ...p, writeBackMode: e.target.value as 'automatic' | 'manual' }))}
                            >
                                <option value="automatic">Written by Vault Sentinel (needs write access to the ID field)</option>
                                <option value="manual">Entered by your change team, from the ServiceNow updates list</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-4 pt-2 pb-2">
                            <Label className="text-sm whitespace-nowrap">Auto-sync every</Label>
                            <Input
                                type="number"
                                min="0"
                                className="w-20"
                                value={configToEdit.syncInterval ?? 0}
                                onChange={(e) => setFormConfig(p => ({ ...p, syncInterval: parseInt(e.target.value, 10) || 0 }))}
                            />
                            <select
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                                value={configToEdit.syncIntervalUnit || 'hours'}
                                onChange={(e) => setFormConfig(p => ({ ...p, syncIntervalUnit: e.target.value as 'minutes' | 'hours' | 'days' }))}
                            >
                                <option value="minutes">minutes</option>
                                <option value="hours">hours</option>
                                <option value="days">days</option>
                            </select>
                            <span className="text-xs text-muted-foreground">Set to 0 to disable auto-sync</span>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={!!configToEdit.enabled}
                                        onCheckedChange={(v) => setFormConfig(p => ({ ...p, enabled: v }))}
                                    />
                                    <Label>Integration enabled</Label>
                                </div>
                                <SetupGuideDialog />
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => { setShowConfig(false); setFormConfig({}) }}>Cancel</Button>
                                <Button onClick={() => saveConfigMutation.mutate(configToEdit)} disabled={saveConfigMutation.isPending}>
                                    Save Configuration
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Tabs defaultValue="items">
                <TabsList>
                    <TabsTrigger value="items">Configuration Items</TabsTrigger>
                    <TabsTrigger value="relationships">Relationships</TabsTrigger>
                </TabsList>

                <TabsContent value="items" className="mt-4 space-y-6">

            {(stats?.serviceNowPending ?? 0) > 0 && (
                <button type="button" onClick={() => setShowUpdates(true)}
                        className="flex w-full items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900 hover:bg-amber-100">
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="flex-1">
                        <strong>{stats?.serviceNowPending}</strong> ServiceNow update{stats?.serviceNowPending === 1 ? '' : 's'} waiting — Vault Sentinel IDs to enter on (or clear from) CIs in ServiceNow.
                    </span>
                    <span className="font-medium underline">View list</span>
                </button>
            )}

            {/* Stats cards */}
            <div className="grid grid-cols-5 gap-4">
                {[
                    { label: 'New', filter: 'new', value: stats?.new ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Changed', filter: 'changed', value: stats?.changed ?? 0, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Removed from MVC', filter: 'deleted', value: stats?.deleted ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
                    { label: 'Synced', filter: 'synced', value: stats?.synced ?? 0, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Total CIs', filter: 'all', value: stats?.total ?? 0, color: 'text-foreground', bg: 'bg-muted/50' },
                ].map((s) => (
                    <Card key={s.label} className={`${s.bg} border-0 cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => { setStatusFilter(s.filter); setPage(1) }}>
                        <CardContent className="p-4">
                            <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
                            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                            {s.filter === 'new' && (stats?.unmatched ?? 0) > 0 && (
                                <p className="text-xs text-amber-700">{stats?.unmatched} not matched</p>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Sync progress banner */}
            {(syncMutation.isPending || lastSync?.status === 'running') && (
                <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <RefreshCw className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-blue-800">Syncing with ServiceNow CMDB...</p>
                                <p className="text-xs text-blue-600 mt-0.5">Fetching configuration items from ServiceNow CMDB. This may take a moment for large CMDBs.</p>
                                <div className="mt-2 h-1.5 w-full bg-blue-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{ width: '60%' }} />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Last sync info */}
            {lastSync && lastSync.status !== 'running' && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Database className="h-4 w-4" />
                    Last sync: {lastSync.status === 'completed' ? (
                        <span>{new Date(lastSync.completedAt!).toLocaleString('en-GB')} — {lastSync.totalCIs} CIs, {lastSync.newItems} new, {lastSync.changedItems} changed, {lastSync.deletedItems} deleted</span>
                    ) : (
                        <span className="text-red-600">Failed: {lastSync.errorMessage}</span>
                    )}
                </div>
            )}

            {/* Filter tabs — segmented toggle matching Card/Table pattern */}
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/30 gap-1">
                {['all', 'new', 'changed', 'deleted', 'synced', 'ignored', 'removed'].map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => { setStatusFilter(s); setPage(1) }}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            statusFilter === s
                                ? 'bg-background shadow-sm text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {FILTER_LABELS[s] ?? s}
                    </button>
                ))}
            </div>
            {canUpdate && statusFilter === 'deleted' && (
                <div className="flex items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                    <span>CIs that left the MVC before they were added have nothing in Vault Sentinel to remove.</span>
                    <Button size="sm" variant="outline" onClick={() => dismissNeverAddedMutation.mutate()} disabled={dismissNeverAddedMutation.isPending}>
                        Dismiss all never added
                    </Button>
                </div>
            )}

            {/* Items table */}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="whitespace-nowrap">Status</TableHead>
                            <TableHead>Sys ID</TableHead>
                            <TableHead>CI Name</TableHead>
                            <TableHead>Class</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Operational Status</TableHead>
                            <TableHead>Vault Sentinel object</TableHead>
                            {canUpdate && <TableHead className="w-[220px]">Actions</TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {itemsLoading ? (
                            <TableRow>
                                <TableCell colSpan={canUpdate ? 8 : 7} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={canUpdate ? 8 : 7} className="text-center py-8 text-muted-foreground">
                                    {config?.enabled ? 'No items. Click "Sync Now" to fetch from ServiceNow.' : 'Configure and enable the ServiceNow integration to get started.'}
                                </TableCell>
                            </TableRow>
                        ) : items.map((item: CMDBSyncItem) => {
                            const badge = STATUS_BADGES[item.syncStatus] || STATUS_BADGES.new
                            return (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Badge variant={badge.variant} className="text-xs whitespace-nowrap">{badge.label}</Badge>
                                    </TableCell>
                                    <TableCell className="text-xs font-mono text-muted-foreground max-w-[120px] truncate" title={item.sysId}>{item.sysId?.substring(0, 12)}...</TableCell>
                                    <TableCell className="font-medium">{item.ciName || '—'}</TableCell>
                                    <TableCell className="text-muted-foreground text-xs">{item.ciClass || '—'}</TableCell>
                                    <TableCell className="text-xs">{item.ciLocation || '—'}</TableCell>
                                    <TableCell className="text-xs">{item.ciOperationalStatus || '—'}</TableCell>
                                    <TableCell className="text-xs">
                                        {item.matched ? (
                                            <span className="inline-flex flex-col">
                                                <span className="inline-flex items-center gap-1 font-mono text-green-700">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> {item.matchedObjectId}
                                                </span>
                                                {item.snPending === 'set' && (
                                                    <button type="button" onClick={() => setShowUpdates(true)} className="inline-flex items-center gap-1 text-amber-700 hover:underline">
                                                        <Clock className="h-3 w-3" /> Waiting for ServiceNow
                                                    </button>
                                                )}
                                            </span>
                                        ) : ['new', 'changed', 'deleted'].includes(item.syncStatus) ? (
                                            <span className="inline-flex flex-col">
                                                <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                                                    <AlertTriangle className="h-3.5 w-3.5" /> Not matched
                                                </span>
                                                {item.suggestedObjectId && (
                                                    <span className="text-muted-foreground">Same name: <span className="font-mono">{item.suggestedObjectId}</span></span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="inline-flex flex-col">
                                                <span className="text-muted-foreground" title={item.changeDetails || ''}>{item.syncStatus === 'removed' ? (item.changeDetails || 'Removed') : '—'}</span>
                                                {item.snPending === 'clear' && (
                                                    <button type="button" onClick={() => setShowUpdates(true)} className="inline-flex items-center gap-1 text-amber-700 hover:underline">
                                                        <Clock className="h-3 w-3" /> ServiceNow still carries our ID
                                                    </button>
                                                )}
                                            </span>
                                        )}
                                    </TableCell>
                                    {canUpdate && (
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {/* Match is the gate: Add, Apply and Remove each change a Vault Sentinel
                                                object, and until the CI is matched we do not know which one. */}
                                            {['new', 'changed'].includes(item.syncStatus) && !item.matched && (
                                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMatching(item)}>
                                                    <Link2 className="h-3 w-3 mr-1" /> Match
                                                </Button>
                                            )}
                                            {item.syncStatus === 'new' && (
                                                <>
                                                    <Button size="sm" variant={item.matched ? 'default' : 'outline'} className="h-7 text-xs"
                                                            disabled={!item.matched || addMutation.isPending}
                                                            title={item.matched
                                                                ? (config?.writeBackMode === 'manual'
                                                                    ? `Link ${item.matchedObjectId} with this CI, and list its ID for your change team to enter in ServiceNow`
                                                                    : `Write ${item.matchedObjectId} to this CI in ServiceNow and link them`)
                                                                : MATCH_FIRST}
                                                            onClick={() => addMutation.mutate(item.id)}>
                                                        <ArrowLeftRight className="h-3 w-3 mr-1" /> Align
                                                    </Button>
                                                    {item.matched && (
                                                        <Button size="sm" variant="ghost" className="h-7 text-xs" title="Unmatch" onClick={() => unmatchMutation.mutate(item.id)}>
                                                            <Unlink className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs" title="Ignore" onClick={() => ignoreMutation.mutate(item.id)}>
                                                        <EyeOff className="h-3 w-3" />
                                                    </Button>
                                                </>
                                            )}
                                            {item.syncStatus === 'changed' && (
                                                <>
                                                    <Button size="sm" variant="outline" className="h-7 text-xs"
                                                            disabled={!item.matched || updateMutation.isPending}
                                                            title={item.matched ? (item.changeDetails || '') : MATCH_FIRST}
                                                            onClick={() => updateMutation.mutate(item.id)}>
                                                        <ArrowUpDown className="h-3 w-3 mr-1" /> Apply
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs" title="Ignore" onClick={() => ignoreMutation.mutate(item.id)}>
                                                        <EyeOff className="h-3 w-3" />
                                                    </Button>
                                                </>
                                            )}
                                            {item.syncStatus === 'deleted' && (
                                                <>
                                                    {item.wasAdded && !item.matched && (
                                                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMatching(item)}>
                                                            <Link2 className="h-3 w-3 mr-1" /> Match
                                                        </Button>
                                                    )}
                                                    {item.wasAdded && (
                                                        <Button size="sm" variant="destructive" className="h-7 text-xs"
                                                                disabled={!item.matched}
                                                                title={item.matched ? `Take ${item.matchedObjectId} out of the MVC` : MATCH_FIRST}
                                                                onClick={() => setRemoving(item)}>
                                                            <LogOut className="h-3 w-3 mr-1" /> Remove
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs"
                                                            title={item.wasAdded ? 'Dismiss: leave Vault Sentinel as it is' : 'Never added — nothing to remove'}
                                                            onClick={() => dismissMutation.mutate(item.id)}>
                                                        Dismiss
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                    )}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {totalItems > 50 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, totalItems)} of {totalItems}</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={page * 50 >= totalItems} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </div>
                </div>
            )}

            <MatchCIDialog item={matching} onOpenChange={(open) => { if (!open) setMatching(null) }} />
            <ServiceNowUpdatesDialog open={showUpdates} onOpenChange={setShowUpdates} />

            <Dialog open={!!removing} onOpenChange={(open) => { if (!open) setRemoving(null) }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Take {removing?.matchedObjectId} out of the MVC?</DialogTitle>
                        <DialogDescription>
                            {removing?.ciName} is no longer in the MVC in ServiceNow. Removing it takes its dependencies out of the
                            recovery hierarchy and clears its link to the CI. The object and its vaulted versions stay.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-60 overflow-auto rounded-md border text-sm">
                        {removePreviewLoading ? (
                            <p className="p-3 text-muted-foreground">Loading dependencies…</p>
                        ) : (removePreview?.dependencies.length ?? 0) === 0 ? (
                            <p className="p-3 text-muted-foreground">It has no dependencies, so only the link is cleared.</p>
                        ) : (
                            <ul className="divide-y">
                                {removePreview!.dependencies.map((d, i) => (
                                    <li key={i} className="px-3 py-1.5">
                                        <span className="font-mono">{d.dependent}</span> depends on <span className="font-mono">{d.dependsOn}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemoving(null)}>Cancel</Button>
                        <Button variant="destructive" disabled={removeMutation.isPending || removePreviewLoading}
                                onClick={() => removing && removeMutation.mutate(removing.id)}>
                            {removeMutation.isPending ? 'Removing…' : `Remove ${removePreview?.dependencies.length ?? 0} dependencies`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

                </TabsContent>

                <TabsContent value="relationships" className="mt-4">
                    <RelationshipsTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}
